import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { collection, doc, setDoc, deleteDoc, onSnapshot, addDoc, query, orderBy } from 'firebase/firestore';
import { db } from '../lib/firebase';

const DataContext = createContext();

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) throw new Error('useData must be used within a DataProvider');
  return context;
};

export const DataProvider = ({ children }) => {
  const [students, setStudents] = useState([]);
  const [sessions, setSessions] = useState({});
  const [parents, setParents] = useState([]);
  const [users, setUsers] = useState([]);
  const [tutorReviews, setTutorReviews] = useState([]);
  const [studentReviews, setStudentReviews] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [messageTemplates, setMessageTemplates] = useState([]);
  const [customSubjects, setCustomSubjects] = useState([]);
  const [communicationLogs, setCommunicationLogs] = useState([]);
  const [appSettings, setAppSettings] = useState({
    logoUrl: '',
    companyName: 'ExpertGuru',
    brandColor: '#4f46e5',
    currency: 'USD',
    currencySymbol: '$',
    exchangeRate: 1,
  });
  const [dataLoaded, setDataLoaded] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const grades = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', 'Undergrad'];
  const statuses = ['Active', 'Trial', 'Idle', 'Hold', 'Finished'];
  const currencies = [
    { code: 'USD', name: 'US Dollar', symbol: '$', rate: 1 },
    { code: 'GBP', name: 'British Pound', symbol: '£', rate: 0.79 },
    { code: 'EUR', name: 'Euro', symbol: '€', rate: 0.92 },
    { code: 'INR', name: 'Indian Rupee', symbol: '₹', rate: 83.00 },
    { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$', rate: 1.35 },
    { code: 'AUD', name: 'Australian Dollar', symbol: 'A$', rate: 1.52 },
    { code: 'AED', name: 'UAE Dirham', symbol: 'د.إ', rate: 3.67 }
  ];

  const subjects = [...new Set(['Math', 'Science', 'English', 'AP Subjects', 'College Subjects', ...customSubjects.map(s => s.name)])];

  useEffect(() => {
    if (!db) return;
    let mounted = true;
    const unsubs = [];

    const collectionsToSync = [
      { name: 'students', setter: setStudents },
      { name: 'parents', setter: setParents },
      { name: 'users', setter: setUsers },
      { name: 'tutorReviews', setter: setTutorReviews },
      { name: 'studentReviews', setter: setStudentReviews },
      { name: 'transactions', setter: setTransactions },
      { name: 'messageTemplates', setter: setMessageTemplates },
      { name: 'subjects', setter: setCustomSubjects },
      { name: 'communicationLogs', setter: setCommunicationLogs }
    ];

    collectionsToSync.forEach(col => {
      const q = query(collection(db, col.name));
      unsubs.push(onSnapshot(q, (snap) => {
        if (mounted) col.setter(snap.docs.map(d => ({ ...d.data(), id: d.id })));
      }));
    });

    unsubs.push(onSnapshot(collection(db, 'sessions'), (snap) => {
      if (!mounted) return;
      const map = {};
      snap.docs.forEach(d => {
        const data = d.data();
        if (!data.studentId || !data.month) return;
        if (!map[data.studentId]) map[data.studentId] = {};
        if (!map[data.studentId][data.month]) map[data.studentId][data.month] = [];
        map[data.studentId][data.month].push({ ...data, id: d.id });
      });
      setSessions(map);
    }));

    unsubs.push(onSnapshot(doc(db, 'appSettings', 'default'), (d) => {
      if (d.exists() && mounted) setAppSettings(prev => ({ ...prev, ...d.data() }));
    }));

    setDataLoaded(true);

    return () => {
      mounted = false;
      unsubs.forEach(u => u());
    };
  }, [refreshTrigger]);

  const formatCurrency = (amt) => `${appSettings.currencySymbol || '$'}${(parseFloat(amt || 0) * (appSettings.exchangeRate || 1)).toFixed(2)}`;

  const calculateRemainingBalance = (sid) => {
    const studentSessions = sessions[sid] || {};
    let unpaid = 0;
    Object.values(studentSessions).forEach(monthArray => {
      monthArray.forEach(s => {
        if (!s.isPaid) unpaid += parseFloat(s.feePerSession || 0);
      });
    });
    return unpaid;
  };

  /**
   * Global Dashboard Stats Calculator
   */
  const calculateDashboardStats = () => {
    let totalRevenue = 0;
    let totalSessions = 0;
    let totalPaidSessions = 0;

    Object.values(sessions).forEach(studentSessions => {
      Object.values(studentSessions).forEach(monthArray => {
        monthArray.forEach(s => {
          totalSessions++;
          if (s.isPaid) {
            totalPaidSessions++;
            totalRevenue += parseFloat(s.feePerSession || 0);
          }
        });
      });
    });

    return {
      totalRevenue,
      totalSessions,
      totalPaidSessions,
      activeStudents: students.filter(s => s.status === 'Active').length
    };
  };

  const value = {
    students,
    sessions,
    parents,
    users,
    tutorReviews,
    studentReviews,
    transactions,
    messageTemplates,
    appSettings,
    months,
    grades,
    statuses,
    subjects,
    currencies,
    dataLoaded,
    communicationLogs,
    forceRefresh: () => setRefreshTrigger(prev => prev + 1),
    calculateRemainingBalance,
    calculateDashboardStats, // <--- ADDED THIS
    formatCurrency,
    addStudent: async (d) => {
      const ref = doc(collection(db, 'students'));
      await setDoc(ref, { ...d, id: ref.id, createdAt: new Date().toISOString() });
    },
    updateStudent: (id, d) => setDoc(doc(db, 'students', id), d, { merge: true }),
    deleteStudent: (id) => deleteDoc(doc(db, 'students', id)).then(() => ({ success: true })),
    addParent: async (d) => {
      const ref = doc(collection(db, 'parents'));
      await setDoc(ref, { ...d, id: ref.id });
      return { ...d, id: ref.id };
    },
    getParentById: (id) => parents.find(p => p.id === id),
    addSession: async (sid, m, d) => {
      const ref = doc(collection(db, 'sessions'));
      await setDoc(ref, { ...d, id: ref.id, studentId: sid, month: m });
    },
    removeSession: (id) => deleteDoc(doc(db, 'sessions', id)).then(() => ({ success: true })),
    updateSessionPayment: (id, isPaid) => setDoc(doc(db, 'sessions', id), { isPaid }, { merge: true }),
    getStudentSessions: (sid) => sessions[sid] || {},
    calculateTotalSessions: (sid) => Object.values(sessions[sid] || {}).reduce((acc, m) => acc + m.length, 0),
    calculatePaidSessions: (sid) => {
      let count = 0;
      Object.values(sessions[sid] || {}).forEach(m => m.forEach(s => s.isPaid && count++));
      return count;
    },
    calculateUnpaidSessions: (sid) => {
      let count = 0;
      Object.values(sessions[sid] || {}).forEach(m => m.forEach(s => !s.isPaid && count++));
      return count;
    },
    getTutorReviews: (name) => tutorReviews.filter(r => r.tutorName === name),
    addTutorReview: (r) => addDoc(collection(db, 'tutorReviews'), r),
    addStudentReview: (r) => addDoc(collection(db, 'studentReviews'), r),
    addCommunicationLog: (log) => addDoc(collection(db, 'communicationLogs'), { ...log, createdAt: new Date().toISOString() }),
    deleteCommunicationLog: (id) => deleteDoc(doc(db, 'communicationLogs', id)),
    saveAppSettings: (s) => setDoc(doc(db, 'appSettings', 'default'), s, { merge: true }),
    addSubject: async (name) => {
      const ref = doc(collection(db, 'subjects'));
      await setDoc(ref, { name, isDefault: false, createdAt: new Date().toISOString() });
      return { success: true };
    },
    deleteSubject: async (name) => {
      const sub = customSubjects.find(s => s.name === name);
      if (sub) await deleteDoc(doc(db, 'subjects', sub.id));
      return { success: true };
    },
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};